import React, { useEffect, useRef, useState } from 'react';

/**
 * What Content → Analytics and Reach → Analytics share ("TinyCMS UX
 * optimization review", App Prototype): the range switch, number tiles, and
 * the grey shapes drawn where numbers TinyCMS doesn't have yet will go
 * (never made-up numbers). A tag over them is only ever something the person
 * can act on (connect Google Analytics) or what's happening ("Bringing in
 * your numbers"), never a "Coming soon" note (owner, 2026-10-22).
 */

export type Days = 7 | 28 | 90;
const RANGES: Array<[Days, string]> = [
  [7, '7 days'],
  [28, '28 days'],
  [90, '90 days'],
];

export const RangeSwitch: React.FC<{ days: Days; onChange: (d: Days) => void }> = ({
  days,
  onChange,
}) => (
  <div className="an-range" role="tablist" aria-label="Range">
    {RANGES.map(([d, label]) => (
      <button
        key={d}
        type="button"
        role="tab"
        aria-selected={days === d}
        data-active={days === d || undefined}
        onClick={() => onChange(d)}
      >
        {label}
      </button>
    ))}
  </div>
);

/** A tag over a grey part of a card: "Connected · Bringing in your numbers", a connect link. */
export const Soon: React.FC<{ children?: React.ReactNode; tag: string }> = ({ children, tag }) => (
  <div className="ink-soon-tag">
    <span>{tag}</span>
    {children}
  </div>
);

/** A grey bar the size of a number, for a cell or tile that isn't recorded yet. */
export const Ghost: React.FC<{ width: number | string; height: number | string }> = ({
  width,
  height,
}) => <span className="ink-ghost" style={{ width, height, verticalAlign: -4 }} />;

/** A number tile with nothing to show yet: its label and a grey value (and `chip`, when given). */
export const GhostTile: React.FC<{ label: string; hint: string; chip?: string | null }> = ({
  label,
  hint,
  chip = null,
}) => (
  <div className="an-tile" title={hint}>
    <span className="an-tile-label">{label}</span>
    <span className="an-tile-value">
      <Ghost width={96} height={26} />
    </span>
    <span className="an-tile-sub">
      {chip ? <span className="ink-soon-chip">{chip}</span> : null}
    </span>
  </div>
);

/** Bars of a made-up shape for the grey chart: the same every time, never data. */
const SHAPE = [46, 58, 40, 66, 72, 54, 80, 62, 50, 70, 84, 60, 74];

/** A bar per day (per week over 90 days) and the chart's ends ("Oct 16", "Today"). */
export function chartBars(days: Days, now = new Date()) {
  const weekly = days === 90;
  const bars = weekly ? 13 : days;
  const dayOf = (k: number) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (weekly ? (bars - 1 - k) * 7 : bars - 1 - k));
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };
  return {
    bars,
    unit: weekly ? 'week' : 'day',
    labels: [
      dayOf(0),
      dayOf(Math.floor(bars / 2)),
      weekly ? `Week of ${dayOf(bars - 1)}` : 'Today',
    ],
  };
}

/** The grey bar chart with its date ends, under a tag when there is something to say. */
export const GhostChart: React.FC<{
  days: Days;
  what?: React.ReactNode;
  tag?: string | null;
}> = ({ days, what = null, tag = null }) => {
  const x = chartBars(days);
  return (
    <>
      <div className="an-soon">
        <div
          className="an-bars"
          aria-hidden="true"
          style={{ gap: days === 7 ? 16 : days === 28 ? 4 : 8 }}
        >
          {Array.from({ length: x.bars }, (_, k) => (
            <div key={k} style={{ height: `${SHAPE[k % SHAPE.length]}%` }} />
          ))}
        </div>
        {tag ? <Soon tag={tag}>{what}</Soon> : null}
      </div>
      <div className="an-x">
        {x.labels.map((l) => (
          <span key={l}>{l}</span>
        ))}
      </div>
    </>
  );
};

/** A chart's key: a coloured square and a name per series. */
export const Legend: React.FC<{ items: Array<[string, string]> }> = ({ items }) => (
  <div className="an-legend">
    {items.map(([label, color]) => (
      <span key={label}>
        <i style={{ background: color }} />
        {label}
      </span>
    ))}
  </div>
);

/** "1 visitor", "2 visitors": every analytics count (round 32). */
export const countOf = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** One bar of a stacked chart and what its popover says. */
export interface TipBar {
  key: string;
  /** The day, or "Week of Oct 6". */
  title: string;
  /** "12 visitors". */
  total: string;
  /** One row per series, in the key's order; empty ones may be left out. */
  rows: Array<{ label: string; value: string; color: string }>;
  /** The stacked parts, bottom first. */
  segments: Array<{ key: string; height: number; color: string }>;
}

/**
 * Bars that say their numbers (Round 32 C): a popover opens on hover, keyboard
 * focus or tap (a second tap, Escape or a tap elsewhere closes it), with the
 * day, the total and a row per series with its colour. Bars in the right 40%
 * open it to the left. Each bar is role="img" with the same numbers in its
 * label. Website → Analytics and Social → Analytics both use it.
 */
export const TipBars: React.FC<{ label: string; gap: number; bars: TipBar[] }> = ({
  label,
  gap,
  bars,
}) => {
  const [open, setOpen] = useState<number | null>(null);
  const tapped = useRef(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);
  const tip = open === null ? null : bars[open];
  const center = open === null ? 0 : ((open + 0.5) / bars.length) * 100;
  return (
    <div className="an-bars-wrap" ref={box}>
      <div className="an-bars an-bars-live" role="group" aria-label={label} style={{ gap }}>
        {bars.map((b, i) => (
          <div
            key={b.key}
            role="img"
            tabIndex={0}
            aria-label={`${b.title}: ${b.total}${b.rows.length ? ', ' : ''}${b.rows
              .map((r) => `${r.label} ${r.value}`)
              .join(', ')}`}
            data-open={open === i ? 'true' : undefined}
            onPointerEnter={(e) => {
              if (e.pointerType === 'mouse') setOpen(i);
            }}
            onPointerLeave={(e) => {
              if (e.pointerType === 'mouse') setOpen((o) => (o === i ? null : o));
            }}
            onPointerDown={(e) => {
              tapped.current = e.pointerType !== 'mouse';
            }}
            onClick={() => {
              if (tapped.current) setOpen((o) => (o === i ? null : i));
            }}
            onFocus={() => setOpen(i)}
            onBlur={() => setOpen((o) => (o === i ? null : o))}
          >
            {b.segments.map((s) => (
              <span key={s.key} style={{ height: `${s.height}px`, background: s.color }} />
            ))}
          </div>
        ))}
      </div>
      {tip ? (
        <div
          className="an-tip"
          aria-hidden="true"
          style={center > 60 ? { right: `${100 - center}%` } : { left: `${center}%` }}
          data-side={center > 60 ? 'left' : 'right'}
        >
          <div className="an-tip-title">{tip.title}</div>
          <div className="an-tip-row an-tip-total">
            <span>Total</span>
            <span>{tip.total}</span>
          </div>
          {tip.rows.map((r) => (
            <div key={r.label} className="an-tip-row">
              <span>
                <i style={{ background: r.color }} />
                {r.label}
              </span>
              <span>{r.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
